import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  PointElement, LineElement, Title, Tooltip, Legend
} from 'chart.js';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon, MinusIcon, ExclamationTriangleIcon, SparklesIcon, LockClosedIcon } from '@heroicons/react/24/outline';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const directionIcon = {
  worsening: { icon: ArrowTrendingUpIcon, color: 'text-red-500', bg: 'bg-red-50' },
  improving: { icon: ArrowTrendingDownIcon, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  stable: { icon: MinusIcon, color: 'text-slate-400', bg: 'bg-slate-50' },
};

export default function TrendsPage() {
  const { user } = useAuth();
  const [trends, setTrends] = useState([]);
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/trends')
      .then(res => {
        setTrends(res.data);
        if (res.data.length > 0) setSelected(res.data[0]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) return;
    api.get(`/trends/${encodeURIComponent(selected.normalizedName)}`)
      .then(res => setHistory(res.data))
      .catch(() => {});
  }, [selected]);

  if (user?.subscriptionStatus === 'starter') {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-center">
        <div className="w-14 h-14 bg-sky-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <ArrowTrendingUpIcon className="w-7 h-7 text-sky-500" />
        </div>
        <h2 className="text-xl font-semibold text-slate-800 mb-2">Trend Tracking requires Full Access</h2>
        <p className="text-slate-500 text-sm mb-6 max-w-sm mx-auto">
          See how your lab values change over time and get alerts when things are trending in the wrong direction. Available on the Full Access plan.
        </p>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 bg-sky-600 text-white px-6 py-2.5 rounded-xl font-semibold text-sm hover:bg-sky-700 transition-colors"
        >
          <SparklesIcon className="w-4 h-4" />
          Upgrade to Full Access — $25/mo
        </Link>
      </div>
    );
  }

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-400">Loading trends…</div>;

  if (trends.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 text-center">
        <ArrowTrendingUpIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <h2 className="text-lg font-semibold text-slate-600">No Trends Yet</h2>
        <p className="text-slate-400 mt-1 text-sm">Upload at least 2 lab reports with the same tests to start seeing trends.</p>
      </div>
    );
  }

  const chartData = {
    labels: history.map(h => new Date(h.reportDate).toLocaleDateString()),
    datasets: [{
      label: selected?.testName || selected?.normalizedName,
      data: history.map(h => h.value),
      borderColor: '#0ea5e9',
      backgroundColor: 'rgba(14, 165, 233, 0.1)',
      pointBackgroundColor: history.map(h =>
        h.trendDirection === 'worsening' ? '#ef4444' :
        h.trendDirection === 'improving' ? '#22c55e' : '#94a3b8'
      ),
      pointRadius: 5,
      tension: 0.3,
      fill: true,
    }],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.raw} ${selected?.unit || ''}`,
        },
      },
    },
    scales: {
      y: { grid: { color: '#f1f5f9' }, ticks: { color: '#94a3b8' } },
      x: { grid: { display: false }, ticks: { color: '#94a3b8' } },
    },
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-800 dark:text-white">Longitudinal Trends</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Track how your lab values are changing over time.</p>
      </div>

      {/* Alerts */}
      {trends.some(t => t.hasAlert) && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-800 text-sm">Trending Alerts</p>
              {trends.filter(t => t.hasAlert).map((t, i) => (
                <p key={i} className="text-sm text-amber-700 mt-1">{t.alertMessage}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Test list */}
        <div className="lg:col-span-1">
          <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Tracked Tests</h2>
          <div className="space-y-2">
            {trends.map((t) => {
              const dir = directionIcon[t.direction] || directionIcon.stable;
              const Icon = dir.icon;
              return (
                <button
                  key={t.normalizedName}
                  onClick={() => setSelected(t)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${
                    selected?.normalizedName === t.normalizedName
                      ? 'border-sky-400 bg-sky-50 dark:bg-sky-900/30'
                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${dir.bg}`}>
                    <Icon className={`w-4 h-4 ${dir.color}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{t.testName || t.normalizedName}</p>
                    <p className="text-xs text-slate-400 dark:text-slate-500">{t.latestValue} {t.unit} · {t.dataPoints} readings</p>
                  </div>
                  {t.hasAlert && <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0 animate-pulse-slow" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Chart */}
        <div className="lg:col-span-2">
          {selected && (
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="font-semibold text-slate-800 dark:text-white">{selected.testName || selected.normalizedName}</h2>
                  <p className="text-sm text-slate-400 dark:text-slate-500 mt-0.5">
                    Latest: <span className="font-medium text-slate-700 dark:text-slate-200">{selected.latestValue} {selected.unit}</span>
                    {' · '}{selected.dataPoints} data points
                  </p>
                </div>
                <span className={`px-2 py-1 rounded-lg text-xs font-medium capitalize ${
                  selected.direction === 'worsening' ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400' :
                  selected.direction === 'improving' ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' :
                  'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                }`}>
                  {selected.direction}
                </span>
              </div>
              {history.length >= 2 ? (
                <Line data={chartData} options={chartOptions} />
              ) : (
                <p className="text-sm text-slate-400 text-center py-8">Need at least 2 data points to show a chart.</p>
              )}
              {selected.alertMessage && (
                <div className="mt-4 bg-amber-50 border border-amber-100 rounded-lg p-3 text-sm text-amber-700">
                  <strong>Alert:</strong> {selected.alertMessage}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
